import React, { useEffect, useState } from "react";
import { AdminHeader } from "../components/Common/CompanyAdminHeader";
import { Button } from "../components/UI/Button";
import { useAuthStore } from "../stores/auth-store";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { NavigateFunction } from "react-router-dom";
import { useUserStore } from "../stores/user-store";
import ErrorMessage from "../components/UI/ErrorMessage";
import { useForm } from "react-hook-form";
import type { FieldErrors } from "react-hook-form";
import ToggleableButton from "../components/UI/ToggleagleButton";
import TextInput from "../components/UI/TextInput";

/** 베이스 페이지 */
const UpdateProfile2: React.FC = () => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode");

  const [
    isPasswordVerificationSuccessful,
    setIsPasswordVerificationSuccessful,
  ] = useState<boolean>(false); // 비밀번호 확인 성공 여부
  const [pageName, setPageName] = useState("프로필 수정"); // 페이지 이름

  const {user} = useAuthStore();
  
  // 버튼 이벤트 핸들러
  const handleBackClick = () => {
    navigate(-1);
  };
  const handleLogoClick = () => {
    navigate(`/`);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* 공용 헤더 사용 */}
      <AdminHeader onLogoClick={handleLogoClick} showMyPageLink={true} />

      {/* 메인 콘텐츠 */}
      <div className="flex flex-col items-center / mt-10">
        <div className="w-full max-w-[750px] / px-5">
          <div className="flex flex-row items-center gap-5 / w-[100%] / mb-6">
            <div role="button" onClick={handleBackClick}>
              <svg
                width="8"
                height="12"
                viewBox="0 0 8 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.12095 0.170067C7.32709 0.376209 7.34583 0.698787 7.17717 0.926099L7.12095 0.991223L2.11243 6L7.12095 11.0088C7.32709 11.2149 7.34583 11.5375 7.17717 11.7648L7.12095 11.8299C6.91481 12.0361 6.59223 12.0548 6.36492 11.8862L6.29979 11.8299L0.880438 6.41058C0.674296 6.20444 0.655556 5.88186 0.824217 5.65455L0.880438 5.58942L6.29979 0.170067C6.52655 -0.056689 6.89419 -0.056689 7.12095 0.170067Z"
                  fill="#252525"
                />
              </svg>
            </div>
            <h2 className="text-xl md:text-2xl font-bold">{pageName}</h2>
          </div>

          {mode === "password" ? (
            isPasswordVerificationSuccessful ? (
              <UpdatePassword2 navigate={navigate} setPageName={setPageName} />
            ) : (
              <UpdatePassword1
                setPageName={setPageName}
                setIsPasswordVerificationSuccessful={
                  setIsPasswordVerificationSuccessful
                }
              />
            )
          ) : null}
          {mode === "name" ? (
            <UpdateName navigate={navigate} setPageName={setPageName} />
          ) : null}
          {mode === "mobile" ? (
            <UpdateMobile navigate={navigate} setPageName={setPageName} />
          ) : null}
        </div>
      </div>
    </div>
  );
};

/** 기존 비밀번호를 확인하는 페이지. */
const UpdatePassword1: React.FC<{
  setPageName: React.Dispatch<React.SetStateAction<string>>;
  setIsPasswordVerificationSuccessful: React.Dispatch<
    React.SetStateAction<boolean>
  >;
}> = ({ setPageName, setIsPasswordVerificationSuccessful }) => {
  const [errorMessage, setErrorMessage] = useState("");
  const { verifyPassword } = useAuthStore();

  // 페이지 이름 변경
  useEffect(() => {
    setPageName("비밀번호 확인");
  }, [setPageName]);

  interface UpdatePassword1Data {
    existingPassword: string;
  }
  const { register, handleSubmit, watch } = useForm<UpdatePassword1Data>({
    shouldUnregister: false,
  });
  const existingPasswordWatch = watch("existingPassword");

  const [isInputEmpty, setIsInputEmpty] = useState(true);
  useEffect(() => {
    if (existingPasswordWatch?.length > 0) {
      setIsInputEmpty(false);
    } else {
      setIsInputEmpty(true);
    }
  }, [existingPasswordWatch]);

  // 양식 제출시 실행되는 함수.
  const onSubmit = async (data: UpdatePassword1Data) => {
    const inputPassword = data.existingPassword;
    const isPasswordMatching = await verifyPassword({
      password: inputPassword,
    });

    if (isPasswordMatching) {
      setIsPasswordVerificationSuccessful(true);
    } else {
      setErrorMessage("비밀번호가 일치하지 않습니다.");
    }
  };
  const onError = (errors: FieldErrors<UpdatePassword1Data>) => {
    const keys = Object.keys(errors) as (keyof UpdatePassword1Data)[];

    for (const key of keys) {
      const error = errors[key];
      if (error) {
        setErrorMessage(
          error.message ? String(error.message) : "입력값을 확인해주세요."
        );
        break;
      }
    }
  };

  return (
    <>
      <h3 className="w-full / text-[14px] md:text-base text-left font-medium / mb-3">
        비밀번호 변경을 위해 현재 비밀번호를 입력해주세요.
      </h3>
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <TextInput
          {...register("existingPassword", {
            required: {
              value: true,
              message: "비밀번호를 입력해주세요.",
            },
          })}
          error={{
            message: errorMessage || "",
            isIconVisible: true,
            isMessageVisible: true,
          }}
          type="password"
          placeholder="비밀번호 입력"
          className="mb-[0.9375rem]"
        />
        <div className="w-full / flex flex-row justify-end">
          <ToggleableButton className="text-[14px] md:text-base w-[200px]" isEnabled={!isInputEmpty}>
            확인
          </ToggleableButton>
        </div>
      </form>
    </>
  );
};

/** 비밀번호를 변경하는 페이지 */
const UpdatePassword2: React.FC<{
  setPageName: React.Dispatch<React.SetStateAction<string>>;
  navigate: NavigateFunction;
}> = ({ setPageName, navigate }) => {
  const [errorMessage, setErrorMessage] = useState("");
  const { fetchProfile, profile, updateProfile } = useUserStore();
  const { user } = useAuthStore();
  const { verifyPassword } = useAuthStore();

  const [isPasswordSubmitted, setIsPasswordSubmitted] = useState(false);
  const [isTheTaskDone, setIsTheTaskDone] = useState(false);

  // 페이지 이름 변경
  useEffect(() => {
    setPageName("비밀번호 변경");
  }, [setPageName]);

  interface UpdatePassword2Data {
    newPassword1: string;
    newPassword2: string;
  }
  const { register, handleSubmit, watch } = useForm<UpdatePassword2Data>();
  const newPassword1Watch = watch("newPassword1");
  const newPassword2Watch = watch("newPassword2");

  useEffect(() => {
    if (newPassword1Watch?.length > 0 && newPassword2Watch?.length > 0) {
      setIsInputEmpty(false);
    } else {
      setIsInputEmpty(true);
    }
  }, [newPassword1Watch?.length, newPassword2Watch?.length]);
  const [isInputEmpty, setIsInputEmpty] = useState(true);

  // 양식 제출시 실행되는 함수
  const onSubmit = async (data: UpdatePassword2Data) => {
    if (isTheTaskDone) {
      setIsTheTaskDone(false);
    }

    if (data.newPassword1 === data.newPassword2 && user?.id) {
      setIsPasswordSubmitted(true);
      await fetchProfile(user.id);
    } else {
      setErrorMessage("입력한 비밀번호가 일치하지 않습니다.");
    }
  };
  const onError = (errors: FieldErrors<UpdatePassword2Data>) => {
    const keys = Object.keys(errors) as (keyof UpdatePassword2Data)[];

    for (const key of keys) {
      const error = errors[key];
      if (error) {
        setErrorMessage(
          error.message ? String(error.message) : "입력값을 확인해주세요."
        );
        break;
      }
    }
  };

  // 불러온 프로필과 로컬스토리지의 유저가 같은지 검사하고 그렇다면 정보를 업데이트함.
  useEffect(() => {
    if (isTheTaskDone) return;

    if (profile && isPasswordSubmitted && profile.id === user?.id) {
      (async () => {
        await updateProfile({ password: newPassword1Watch });

        const isPasswordMatching = await verifyPassword({
          password: newPassword1Watch,
        });

        if (isPasswordMatching) {
          navigate(
            "/profile?globalModalMessage=비밀번호가 정상적으로 수정되었습니다."
          );
        } else {
          setErrorMessage("비밀번호가 변경되지 않았습니다.");
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, isPasswordSubmitted, newPassword1Watch]);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <div className="w-full / text-[14px] md:text-base text-left / font-medium / mb-3">
          변경할 비밀번호를 입력해주세요.
        </div>

        <TextInput
          {...register("newPassword1", {
            required: {
              value: true,
              message: "변경할 비밀번호를 입력해 주세요.",
            },
          })}
          error={{
            message: "",
            isIconVisible: true,
            isMessageVisible: false,
          }}
          type="password"
          className="mb-[0.9375rem]"
        />

        <div className="w-full / text-[14px] md:text-base text-left / font-medium / mb-3">
          비밀번호를 다시 한번 입력해주세요.
        </div>

        <TextInput
          {...register("newPassword2", {
            required: {
              value: true,
              message: "확인 비밀번호를 입력해 주세요.",
            },
          })}
          error={{
            message: errorMessage || "",
            isIconVisible: true,
            isMessageVisible: true,
          }}
          type="password"
          className="mb-[0.9375rem]"
        />

        <div className="w-full / flex flex-row justify-end">
          <ToggleableButton className="text-[14px] md:text-base w-[200px]" isEnabled={!isInputEmpty}>
            확인
          </ToggleableButton>
        </div>
      </form>
    </>
  );
};

/** 이름을 변경하는 페이지. */
const UpdateName: React.FC<{
  setPageName: React.Dispatch<React.SetStateAction<string>>;
  navigate: NavigateFunction;
}> = ({ setPageName, navigate }) => {
  const [isTheNameSubmitted, setIsTheNameSubmitted] = useState(false);

  const { fetchProfile, profile, updateProfile } = useUserStore();
  const { user } = useAuthStore();

  // 페이지 이름 변경
  useEffect(() => {
    setPageName("이름 수정");
  }, [setPageName]);

  interface UpdateNameData {
    updatingName: string;
  }
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
    setError
  } = useForm<UpdateNameData>();
  const [errorMessage, setErrorMessage] = useState("");
  const updatingNameWatch = watch("updatingName");

  useEffect(() => {
    setValue( "updatingName", user?.name ?? "");
  }, [setValue, user?.mobile, user?.name]);

  useEffect(() => {
    if (updatingNameWatch?.length > 0) {
      setIsInputEmpty(false);
    } else {
      setIsInputEmpty(true);
    }
  }, [updatingNameWatch?.length]);
  const [isInputEmpty, setIsInputEmpty] = useState(true);

  // 양식 제출시 실행되는 함수
  const onSubmit = async (data: UpdateNameData) => {
    if (user?.id) {
      // 서버에 프로필 업데이트
      try {
        await updateProfile({ name: data.updatingName });

        const currentProfile = useUserStore.getState().profile;
        if (currentProfile) {
          useAuthStore.setState((state) => ({
            ...state,
            user: currentProfile,
          }));
        }

        await fetchProfile(user.id);
        setIsTheNameSubmitted(true);
      } catch (e: unknown) {
        if (e instanceof Error) {
          setError("updatingName", {message: e.message});
        }
        return;
      }
    }
  };
  const onError = (errors: FieldErrors<UpdateNameData>) => {
    const keys = Object.keys(errors) as (keyof UpdateNameData)[];

    for (const key of keys) {
      const error = errors[key];
      if (error) {
        setErrorMessage(
          error.message ? String(error.message) : "입력값을 확인해주세요."
        );
        break;
      }
    }
  };

  // 불러온 프로필과 로컬스토리지의 유저가 같은지 검사하고 그렇다면 정보를 업데이트함.
  useEffect(() => {
    if (!isTheNameSubmitted) return;

    if (profile && profile.id === user?.id) {
      (async () => {
        if (profile.name === user.name) {
          navigate("/profile?globalModalMessage=이름이 정상적으로 수정되었습니다.");
        } else {
          setErrorMessage("이름이 변경되지 않았습니다.");
        }
      })();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, isTheNameSubmitted]);

  return (
    <>
      <h3 className="w-full / text-[14px] md:text-base / text-left font-bold / mb-3">
        변경하실 이름을 입력해주세요.
      </h3>
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <TextInput
          {...register("updatingName", {
            required: {
              value: true,
              message: "변경할 이름을 입력해주세요.",
            },
          })}
          error={{
            message: errorMessage || errors.updatingName?.message || "",
            isIconVisible: true,
            isMessageVisible: true,
          }}
          placeholder="이름 입력"
          type="text"
          className="mb-[0.9375rem]"
        />

        <div className="w-full / flex flex-row justify-end">
          <ToggleableButton className="text-[14px] md:text-base w-[200px]" isEnabled={!isInputEmpty}>
            확인
          </ToggleableButton>
        </div>
      </form>
    </>
  );
};

/** 휴대폰 번호를 변경하는 페이지. */
const UpdateMobile: React.FC<{
  setPageName: React.Dispatch<React.SetStateAction<string>>;
  navigate: NavigateFunction;
}> = ({ setPageName, navigate }) => {
  const [isTheMobileSubmitted, setIsTheNameSubmitted] = useState(false);

  const { fetchProfile, profile, updateProfile } = useUserStore();
  const { user } = useAuthStore();

  const [isInputEmpty, setIsInputEmpty] = useState(true);
  const [nameInput, setNameInput] = useState("");

  // 페이지 이름 변경
  useEffect(() => {
    setPageName("휴대폰 번호 수정");
  }, [setPageName]);

  interface UpdateMobileData {
    updatingMobile: string;
  }
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
  } = useForm<UpdateMobileData>();
  const [errorMessage, setErrorMessage] = useState("");
  const updatingMobileWatch = watch("updatingMobile");

  useEffect(() => {
    if (updatingMobileWatch?.length > 0) {
      setIsInputEmpty(false);
    } else {
      setIsInputEmpty(true);
    }
  }, [updatingMobileWatch?.length]);

  useEffect(() => {
    setValue( "updatingMobile", user?.mobile ?? "");
  }, [setValue, user?.mobile]);

  // 양식 제출시 실행되는 함수
  const onSubmit = async (data: UpdateMobileData) => {
    if (user?.id) {
      // 서버에 프로필 업데이트
      await updateProfile({ mobile: data.updatingMobile });

      const currentProfile = useUserStore.getState().profile;
      if (currentProfile) {
        useAuthStore.setState((state) => ({
          ...state,
          user: currentProfile,
        }));
      }

      await fetchProfile(user.id);
      setIsTheNameSubmitted(true);
    }
  };
  const onError = (errors: FieldErrors<UpdateMobileData>) => {
    const keys = Object.keys(errors) as (keyof UpdateMobileData)[];

    for (const key of keys) {
      const error = errors[key];
      if (error) {
        setErrorMessage(
          error.message ? String(error.message) : "입력값을 확인해주세요."
        );
        break;
      }
    }
  };

  // 불러온 프로필과 로컬스토리지의 유저가 같은지 검사하고 그렇다면 정보를 업데이트함.
  useEffect(() => {
    if (!isTheMobileSubmitted) return;

    if (profile && profile.id === user?.id) {
      (async () => {
        if (profile.name === user.name) {
          navigate(
            "/profile?globalModalMessage=전화번호가 정상적으로 수정되었습니다."
          );
        } else {
          setErrorMessage("전화번호가 변경되지 않았습니다.");
        }
      })();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, isTheMobileSubmitted]);

  return (
    <>
      <h3 className="w-full / text-[14px] md:text-base text-left font-bold / mb-3">
        변경하실 휴대폰 번호를 입력해주세요.
      </h3>
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <TextInput
          {...register("updatingMobile", {
            required: {
              value: true,
              message: "변경할 이름을 입력해주세요.",
            },
          })}
          error={{
            message: errorMessage || "",
            isIconVisible: true,
            isMessageVisible: true,
          }}
          placeholder="휴대폰 번호 입력"
          type="number"
          className="mb-[0.9375rem]"
          inputClassName="no-spinner"
        />
        <div className="w-full / flex flex-row justify-end">
          <ToggleableButton className="text-[14px] md:text-base w-[200px]" isEnabled={!isInputEmpty}>
            확인
          </ToggleableButton>
        </div>
      </form>
    </>
  );
};

export default UpdateProfile2;
